/**
 * Pure helpers for Sector Operations Rosters UI grid, paging, cell chips, warnings, and acknowledgements.
 * R39 A6.b (SO-P60, SO-P62).
 */

import type {
  OpsPersonRef,
  OpsRosterAssignment,
  OpsRosterVersion,
  OpsRosterWarning,
  OpsShift,
  OpsWorker,
} from '../types/ops'
import type { WorkspaceUserDirectoryEntry } from '../services/workspaceUserDirectory'

export interface OpsGridPerson {
  key: string
  personRef: OpsPersonRef
  name: string
}

export interface OpsCellChip {
  assignmentId?: string
  shiftId: string
  shiftCode: string
  shiftName?: string
  dutyRoleKey?: string
  kind: 'SHIFT' | 'ON_CALL_PRIMARY' | 'ON_CALL_BACKUP'
  isOvernight: boolean
  hasWarning?: boolean
}

/**
 * Returns a stable string key for a personRef.
 * e.g. 'm_uid123' for MEMBER, 'w_worker456' for WORKER.
 */
export function personKeyFromRef(personRef: OpsPersonRef): string {
  if (personRef.kind === 'MEMBER') {
    return `m_${personRef.uid}`
  }
  return `w_${personRef.workerId || personRef.workerCode || ''}`
}

/**
 * Checks if two personRefs point to the same person.
 */
export function arePersonRefsEqual(a: OpsPersonRef, b: OpsPersonRef): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'MEMBER' && b.kind === 'MEMBER') return a.uid === b.uid
  if (a.kind === 'WORKER' && b.kind === 'WORKER') {
    const aId = a.workerId || a.workerCode
    const bId = b.workerId || b.workerCode
    return Boolean(aId && bId && aId === bId)
  }
  return false
}

/**
 * Generates consecutive YYYY-MM-DD day keys from periodStart to periodEnd.
 * Max length is 42 days (SO-P60).
 */
export function enumeratePeriodDayKeys(periodStart: string, periodEnd: string): string[] {
  if (!periodStart || !periodEnd) return []
  if (periodStart > periodEnd) return []

  const result: string[] = []
  const start = new Date(`${periodStart}T00:00:00Z`)
  const end = new Date(`${periodEnd}T00:00:00Z`)

  const cur = new Date(start)
  while (cur <= end) {
    result.push(cur.toISOString().slice(0, 10))
    if (result.length > 42) {
      throw new Error(`Roster period exceeds 42 days (${result.length} days)`)
    }
    cur.setUTCDate(cur.getUTCDate() + 1)
  }

  return result
}

/**
 * Slices day keys into a 7-day page for the given pageIndex (0-based).
 */
export function pageDayKeys(
  dayKeys: string[],
  pageIndex: number,
  pageSize = 7,
): string[] {
  if (pageIndex < 0 || pageSize <= 0) return []
  const start = pageIndex * pageSize
  return dayKeys.slice(start, start + pageSize)
}

/**
 * Returns total number of pages (each of size 7).
 */
export function getTotalGridPages(totalDays: number, pageSize = 7): number {
  if (totalDays <= 0 || pageSize <= 0) return 1
  return Math.max(1, Math.ceil(totalDays / pageSize))
}

/**
 * Resolves a human-friendly display name for a personRef from workspace user directory or worker list.
 */
export function resolvePersonDisplayName(
  personRef: OpsPersonRef,
  directoryMembers: WorkspaceUserDirectoryEntry[] | Map<string, string>,
  workers: OpsWorker[] | Map<string, string>,
  fallback = 'Unknown',
): string {
  if (personRef.kind === 'MEMBER') {
    if (directoryMembers instanceof Map) {
      const found = directoryMembers.get(personRef.uid)
      if (found) return found
    } else {
      const entry = directoryMembers.find((m) => m.uid === personRef.uid)
      if (entry) {
        return entry.displayName || entry.email || personRef.uid
      }
    }
    return fallback === 'Unknown' ? `Member (${personRef.uid.slice(0, 6)})` : fallback
  }

  if (personRef.kind === 'WORKER') {
    const workerId = personRef.workerId || personRef.workerCode || ''
    if (workers instanceof Map) {
      const found = workers.get(workerId)
      if (found) return found
    } else {
      const w = workers.find((worker) => worker.id === workerId || worker.workerCode === workerId)
      const workerName = w ? (w.displayName || (w as { name?: string }).name) : undefined
      if (workerName) return workerName
    }
    return fallback === 'Unknown' ? `Worker (${workerId.slice(0, 6)})` : fallback
  }

  return fallback
}

/**
 * Extracts unique list of people appearing in assignments, sorted by name.
 */
export function extractGridPeople(
  assignments: OpsRosterAssignment[],
  directoryMembers: WorkspaceUserDirectoryEntry[] | Map<string, string>,
  workers: OpsWorker[] | Map<string, string>,
): OpsGridPerson[] {
  const peopleMap = new Map<string, OpsGridPerson>()

  for (const a of assignments) {
    const key = personKeyFromRef(a.personRef)
    if (!peopleMap.has(key)) {
      const name = resolvePersonDisplayName(a.personRef, directoryMembers, workers)
      peopleMap.set(key, { key, personRef: a.personRef, name })
    }
  }

  return Array.from(peopleMap.values()).sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Builds cell chips for a specific person on a given dayKey.
 * Checks whether the shift crosses midnight (endMinute <= startMinute) and marks isOvernight.
 */
export function getCellChips(
  personRef: OpsPersonRef,
  dayKey: string,
  assignments: OpsRosterAssignment[],
  shifts: OpsShift[] | Map<string, OpsShift>,
  warningsMap?: Map<string, OpsRosterWarning[]>,
): OpsCellChip[] {
  const pKey = personKeyFromRef(personRef)
  const cellKey = `${pKey}_${dayKey}`
  const hasCellWarning = Boolean(warningsMap && (warningsMap.get(cellKey)?.length ?? 0) > 0)

  const shiftsMap =
    shifts instanceof Map
      ? shifts
      : new Map(shifts.map((s) => [s.id, s]))

  const matching = assignments.filter(
    (a) => a.dayKey === dayKey && arePersonRefsEqual(a.personRef, personRef),
  )

  return matching.map((a) => {
    const shift = shiftsMap.get(a.shiftId)
    const shiftCode = shift?.code || shift?.name || a.shiftId
    const shiftName = shift?.name || shiftCode
    // If endMinute <= startMinute, it crosses midnight into the next day (SO-P60)
    const isOvernight = Boolean(shift && shift.endMinute <= shift.startMinute)

    return {
      assignmentId: a.id,
      shiftId: a.shiftId,
      shiftCode,
      shiftName,
      dutyRoleKey: a.dutyRoleKey,
      kind: a.kind,
      isOvernight,
      hasWarning: hasCellWarning,
    }
  })
}

/**
 * Maps warnings to cell keys formatted as `${personKey}_${dayKey}`.
 */
export function mapWarningsToCells(
  warnings: OpsRosterWarning[],
): Map<string, OpsRosterWarning[]> {
  const map = new Map<string, OpsRosterWarning[]>()

  for (const w of warnings) {
    const legacyWarning = w as OpsRosterWarning & { personRef?: OpsPersonRef }
    const personKey =
      w.personKey ||
      (legacyWarning.personRef ? personKeyFromRef(legacyWarning.personRef) : undefined)
    if (personKey && w.dayKey) {
      const key = `${personKey}_${w.dayKey}`
      const existing = map.get(key) || []
      existing.push(w)
      map.set(key, existing)
    }
  }

  return map
}

/**
 * Computes acknowledgement count for a version.
 * SO-P60: Only people with an account (MEMBER kind) are asked to acknowledge.
 */
export function getAcknowledgementStats(version: OpsRosterVersion | undefined): {
  totalAccountCount: number
  ackedCount: number
  isFullyAcked: boolean
} {
  if (!version || !Array.isArray(version.assignments)) {
    return { totalAccountCount: 0, ackedCount: 0, isFullyAcked: false }
  }

  const memberUids = new Set<string>()
  for (const a of version.assignments) {
    if (a.personRef.kind === 'MEMBER' && a.personRef.uid) {
      memberUids.add(a.personRef.uid)
    }
  }

  const totalAccountCount = memberUids.size
  if (totalAccountCount === 0) {
    return { totalAccountCount: 0, ackedCount: 0, isFullyAcked: true }
  }

  let ackedCount = 0
  const acks = version.acks || {}
  for (const uid of memberUids) {
    if (typeof acks[uid] === 'string' && acks[uid].trim()) {
      ackedCount++
    }
  }

  return {
    totalAccountCount,
    ackedCount,
    isFullyAcked: ackedCount >= totalAccountCount,
  }
}

/**
 * Checks whether the current caller is a member assigned in this version and has NOT yet acknowledged.
 */
export function needsCallerAcknowledgement(
  version: OpsRosterVersion | undefined,
  callerUid: string | undefined,
): boolean {
  if (!version || !callerUid || !Array.isArray(version.assignments)) {
    return false
  }

  // Check if caller is assigned as a MEMBER
  const isAssigned = version.assignments.some(
    (a) => a.personRef.kind === 'MEMBER' && a.personRef.uid === callerUid,
  )
  if (!isAssigned) return false

  // Check if already acknowledged
  const acks = version.acks || {}
  const hasAcked = typeof acks[callerUid] === 'string' && Boolean(acks[callerUid].trim())
  return !hasAcked
}

/**
 * Format day string for header display.
 */
export function formatDayHeaderLabels(dayKey: string): { title: string; sub: string } {
  try {
    const d = new Date(`${dayKey}T00:00:00Z`)
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const title = `${weekdays[d.getUTCDay()]} ${d.getUTCDate()}`
    const sub = months[d.getUTCMonth()]
    return { title, sub }
  } catch {
    return { title: dayKey, sub: '' }
  }
}
